import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMarkPaid, useReceipt, useSendReceipt, useVoidReceipt } from './hooks';
import { documentLabel } from './kindLabel';
import { formatAccountMoney } from './accountBalance';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getApiErrorMessage } from '../../api/errorMessage';
import type { PaidMethod } from './types';

export function ReceiptDetailPage() {
  usePageTitle('Receipt');
  const { id = '' } = useParams();
  const { data: receipt, isLoading } = useReceipt(id);
  const send = useSendReceipt();
  const markPaid = useMarkPaid();
  const voidReceipt = useVoidReceipt();

  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [paidForm, setPaidForm] = useState<{ method: PaidMethod; reference: string; amount: string }>({
    method: 'Cash',
    reference: '',
    amount: ''
  });
  // Server-error surfaces — Send/Void render in the Actions section, Mark paid
  // inside the payment form. Without these the fire-and-forget handlers swallow
  // failures (e.g. the 409 when a Final isn't Sent yet) and the button looks dead.
  const [actionError, setActionError] = useState<string | null>(null);
  const [markPaidError, setMarkPaidError] = useState<string | null>(null);

  if (isLoading) return <div className="max-w-3xl mx-auto px-4 py-8 text-slate-500">Loading…</div>;
  if (!receipt)
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-slate-500">
        Receipt not found.{' '}
        <Link to="/receipts" className="text-sky-600">Back to list</Link>
      </div>
    );

  const canSend = receipt.status === 'Draft' || receipt.status === 'Sent';
  // Gate Mark paid to match the v1.4 backend: a Final can be paid only once
  // Sent; a Deposit from Draft or Sent.
  const canMarkPaid =
    receipt.status !== 'Void' &&
    receipt.status !== 'Paid' &&
    (receipt.kind === 'Deposit' || receipt.status === 'Sent');
  // A Final still in Draft can't be paid yet — guide the user instead of
  // offering a button that would 409.
  const showMarkPaidHint = receipt.kind === 'Final' && receipt.status === 'Draft';
  const canVoid = receipt.status !== 'Void';
  // "invoice" | "receipt" | "deposit" — the document's own noun for this state.
  const docNoun = documentLabel(receipt.kind, receipt.status).toLowerCase();

  const onSend = async () => {
    if (!confirm(`Send ${docNoun} ${receipt.receiptNumber} to the customer?`)) return;
    setActionError(null);
    try {
      await send.mutateAsync(receipt.id);
    } catch (e) {
      setActionError(getApiErrorMessage(e, `Couldn't send the ${docNoun} — please try again.`));
    }
  };

  const onMarkPaid = async () => {
    const amount = paidForm.amount.trim() === '' ? null : Number(paidForm.amount);
    setMarkPaidError(null);
    try {
      await markPaid.mutateAsync({
        id: receipt.id,
        payload: { method: paidForm.method, reference: paidForm.reference || null, amount }
      });
      setShowMarkPaid(false); // only advance the UI on success
    } catch (e) {
      setMarkPaidError(getApiErrorMessage(e, "Couldn't mark paid — please try again."));
    }
  };

  const onVoid = async () => {
    const reason = prompt('Void reason (optional):');
    if (reason === null) return;
    setActionError(null);
    try {
      await voidReceipt.mutateAsync({
        id: receipt.id,
        notes: reason || undefined,
        overridePaidCheck: receipt.status === 'Paid'
      });
    } catch (e) {
      setActionError(getApiErrorMessage(e, `Couldn't void the ${docNoun} — please try again.`));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/receipts" className="text-sm text-slate-500 hover:text-sky-600">
            ← Receipts
          </Link>
          <h1 className="text-2xl font-semibold text-slate-800 mt-2 font-mono">
            {receipt.receiptNumber}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {documentLabel(receipt.kind, receipt.status)} • {receipt.customerName} • {new Date(receipt.issuedAtUtc).toLocaleDateString()}
          </p>
        </div>
        <StatusBadge status={receipt.status} />
      </div>

      <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <table className="w-full text-sm">
          <thead className="text-slate-600">
            <tr>
              <th className="text-left font-medium pb-2">Description</th>
              <th className="text-right font-medium pb-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {receipt.lineItems.map((li) => (
              <tr key={li.id} className="border-t border-slate-100">
                <td className="py-2">{li.description}</td>
                <td className="py-2 text-right">{li.lineTotal.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-right text-sm space-y-1 pt-2 border-t border-slate-100">
          <div>Subtotal: {receipt.subtotal.toFixed(2)} {receipt.currency}</div>
          {receipt.adjustments !== 0 && (
            <div>Adjustments: {receipt.adjustments.toFixed(2)} {receipt.currency}</div>
          )}
          <div className="text-lg font-semibold text-slate-800">
            Total: {receipt.total.toFixed(2)} {receipt.currency}
          </div>
        </div>
        {receipt.notes && (
          <div className="text-sm text-slate-600 border-t border-slate-100 pt-3">
            <strong className="text-slate-700">Notes: </strong>
            {receipt.notes}
          </div>
        )}
      </section>

      {receipt.openingBalance != null && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-1.5">
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Account statement</h2>
          {receipt.kind === 'Final' ? (
            <>
              <StatementRow
                label={receipt.openingBalance >= 0 ? 'Amount on deposit (opening)' : 'Previous balance due'}
                value={formatAccountMoney(receipt.openingBalance)}
              />
              <StatementRow label="Total charges" value={`-${formatAccountMoney(receipt.total)}`} />
              <StatementRow label="Deposit applied" value={`-${formatAccountMoney(receipt.depositApplied ?? 0)}`} />
              <div className="border-t border-slate-100 my-1" />
              <StatementRow label="Amount due now" value={formatAccountMoney(receipt.amountDue ?? 0)} bold />
              <ClosingRow closing={receipt.closingBalance ?? 0} />
            </>
          ) : (
            <>
              <StatementRow label="Deposit received" value={formatAccountMoney(receipt.total)} />
              <StatementRow
                label="Amount on deposit"
                value={`${formatAccountMoney(receipt.closingBalance ?? 0)} CR`}
                className="text-green-700"
                bold
              />
            </>
          )}
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h2 className="text-sm font-semibold text-slate-700">Actions</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!canSend || send.isPending}
            onClick={onSend}
            className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
          >
            {send.isPending ? 'Sending…' : receipt.status === 'Sent' ? 'Resend' : `Send ${docNoun}`}
          </button>
          <button
            type="button"
            disabled={!canMarkPaid || markPaid.isPending}
            onClick={() => {
              setPaidForm((f) => ({
                ...f,
                amount: receipt.amountDue != null ? String(receipt.amountDue) : ''
              }));
              setMarkPaidError(null);
              setShowMarkPaid(true);
            }}
            className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 disabled:opacity-50 text-sm"
          >
            Mark paid
          </button>
          <button
            type="button"
            disabled={!canVoid || voidReceipt.isPending}
            onClick={onVoid}
            className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 disabled:opacity-50 text-sm"
          >
            Void
          </button>
        </div>

        {showMarkPaidHint && (
          <p className="text-xs text-slate-500">Send the invoice before you can mark it paid.</p>
        )}

        {actionError && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {actionError}
          </div>
        )}

        {send.isSuccess && (
          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
            Sent — email outbox id{' '}
            <span className="font-mono">{send.data?.result.emailOutboxId.slice(0, 8)}</span>.
            {send.data?.result.smsStatus === 'FeatureDisabled' && (
              <> (SMS queued as FeatureDisabled — email delivered as the active channel.)</>
            )}
          </div>
        )}

        {receipt.status === 'Paid' && (
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
            Marked paid via <strong>{receipt.paidMethod}</strong>
            {receipt.paidAmount != null && <> for <strong>{formatAccountMoney(receipt.paidAmount)}</strong></>}
            {receipt.paidReference && (
              <> (ref <span className="font-mono">{receipt.paidReference}</span>)</>
            )}
            {receipt.paidAtUtc && <> on {new Date(receipt.paidAtUtc).toLocaleDateString()}</>}.
            {receipt.kind === 'Final' && <PaidResidual receipt={receipt} />}
          </div>
        )}
      </section>

      {showMarkPaid && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-700">Record payment</h2>
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Method</span>
            <select
              value={paidForm.method}
              onChange={(e) => setPaidForm((f) => ({ ...f, method: e.target.value as PaidMethod }))}
              className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
            >
              <option>Cash</option>
              <option>Check</option>
              <option>Venmo</option>
              <option>Zelle</option>
              <option>Other</option>
            </select>
          </label>
          {receipt.kind === 'Final' && (
            <label className="block">
              <span className="block text-sm font-medium text-slate-700 mb-1">
                Amount paid
                {receipt.amountDue != null && (
                  <span className="ml-1 text-xs text-slate-400">
                    (due {formatAccountMoney(receipt.amountDue)})
                  </span>
                )}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                max={receipt.amountDue ?? undefined}
                value={paidForm.amount}
                onChange={(e) => setPaidForm((f) => ({ ...f, amount: e.target.value }))}
                className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
                placeholder={receipt.amountDue != null ? String(receipt.amountDue) : '0.00'}
              />
              <span className="mt-1 block text-xs text-slate-400">
                Defaults to the amount due. Enter less to record a partial payment.
              </span>
            </label>
          )}
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Reference</span>
            <input
              type="text"
              value={paidForm.reference}
              onChange={(e) => setPaidForm((f) => ({ ...f, reference: e.target.value }))}
              className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
              placeholder="Check #, Venmo handle, etc."
            />
          </label>
          {markPaidError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {markPaidError}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onMarkPaid}
              disabled={markPaid.isPending}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setShowMarkPaid(false)}
              className="text-slate-600 hover:text-sky-600 text-sm"
            >
              Cancel
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function StatementRow({
  label,
  value,
  bold = false,
  className = 'text-slate-700'
}: {
  label: string;
  value: string;
  bold?: boolean;
  className?: string;
}) {
  return (
    <div className={`flex items-center justify-between text-sm ${bold ? 'font-semibold' : ''}`}>
      <span className="text-slate-600">{label}</span>
      <span className={`font-mono ${className}`}>{value}</span>
    </div>
  );
}

// The resulting running balance line: positive = remaining on deposit (CR),
// negative = balance due.
function ClosingRow({ closing }: { closing: number }) {
  return closing >= 0 ? (
    <StatementRow
      label="Remaining on deposit"
      value={`${formatAccountMoney(closing)} CR`}
      className="text-green-700"
      bold
    />
  ) : (
    <StatementRow label="Balance due" value={formatAccountMoney(closing)} className="text-red-700" bold />
  );
}

// After mark-paid, surface any residual still owed on this receipt
// (amount due minus what was actually paid).
function PaidResidual({ receipt }: { receipt: { amountDue: number | null; paidAmount: number | null } }) {
  const residual = (receipt.amountDue ?? 0) - (receipt.paidAmount ?? 0);
  return residual > 0 ? (
    <div className="mt-1 font-semibold text-red-700">
      Balance still due: {formatAccountMoney(residual)}
    </div>
  ) : (
    <div className="mt-1 font-semibold text-green-700">Settled</div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const palette: Record<string, string> = {
    Draft: 'bg-slate-100 text-slate-700 border-slate-200',
    Sent: 'bg-sky-50 text-sky-800 border-sky-200',
    Paid: 'bg-green-50 text-green-800 border-green-200',
    Void: 'bg-red-50 text-red-800 border-red-200'
  };
  return (
    <span className={`inline-flex items-center text-xs px-3 py-1 rounded border ${palette[status] ?? ''}`}>
      {status}
    </span>
  );
}
